import React from 'react';
import Confetti from 'canvas-confetti';
import { asyncWait } from '@uplift/utils';


/**
 * this method is used to prevent executing interval
 * in the background when the tab is not active
 * as confetti animation is not running in the background as it is driven by requestAnimationFrame
*/
function setIntervalWithAnimationFrame(callback: () => void, interval: number) {
  let start = Date.now();
  let id: number;

  function tick() {
    const now = Date.now();
    const delta = now - start;

    if (delta >= interval) {
      callback();
      start = now - (delta % interval); // Adjust the start time to account for any drift
    }

    id = requestAnimationFrame(tick);
  }

  id = requestAnimationFrame(tick);

  // Return a function to allow clearing the interval
  return {
    clear: () => cancelAnimationFrame(id)
  };
}


const defaultConfig = {
  scalar: 2,
  spread: 50,
  particleCount: 150,
  colors: [ '#F1F861', '#6E30F7', '#78DEE9', '#2136A1', '#A0CDD2' ],
  startVelocity: 90,
  gravity: 4,
};


const asyncDropConfetti = async (confetti: Confetti.CreateTypes) => {
  confetti({
    ...defaultConfig,
    angle: 65,
    origin: { y: 0.9, x: 0.2 },
    drift: 0.6,
  });
  await asyncWait(200);
  confetti({
    ...defaultConfig,
    angle: 115,
    origin: { y: 0.9, x: 0.8 },
    drift: -0.6,
  });
  await asyncWait(400);
  confetti({
    ...defaultConfig,
    angle: 65,
    origin: { y: 0.9, x: 0.2 },
    drift: 0.6,
  });
  await asyncWait(600);
  confetti({
    ...defaultConfig,
    angle: 115,
    origin: { y: 0.9, x: 0.8 },
    drift: -0.6,
  });
};


export const BackgroundAnimation: React.VFC = () => {
  const [ canvas, setCanvas ] = React.useState<HTMLCanvasElement>();

  React.useEffect(() => {
    if (!canvas) return;
    const confetti = Confetti.create(canvas, { resize: true });

    const interval = setIntervalWithAnimationFrame(() => {
      asyncDropConfetti(confetti);
    }, 2500);

    asyncDropConfetti(confetti);

    return () => interval.clear();
  }, [ canvas ]);

  return (
    <canvas
      ref={setCanvas}
      width={window.innerWidth}
      height={window.innerHeight}
      style={{
        position: 'fixed',
        left: 0,
        top: 0,
        width: '100%',
        height: '100%',
      }}
    />
  );
};
